/**
 * The last line before a white screen.
 *
 * Without one of these, any throw anywhere in the render tree unmounts the whole
 * app and leaves a blank page with nothing on it — no message, no button, and on
 * a phone no console to look at. That has already happened twice during
 * development, and both times the only fix was clearing storage from a laptop.
 * On the 12th at Bandon that is not a fix.
 *
 * So this shows what broke, offers the two things that actually recover an app
 * in the field, and — the important part — never lets a bad local document take
 * the scores with it. "Start fresh" clears the cached document and cursor but
 * deliberately KEEPS the outbox, because unsent scores are the one thing here
 * that exists nowhere else.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
}

const KEY = 'woodsmen:v1'

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Nothing to report to; leaving a trail in the console is the only thing
    // that helps whoever plugs this into a laptop afterwards.
    console.error('The Woodsmen crashed:', error, info.componentStack)
  }

  /** Drop the cached snapshot, keep anything not yet sent. */
  private startFresh = () => {
    try {
      const raw = localStorage.getItem(KEY)
      const saved = raw ? JSON.parse(raw) : {}
      localStorage.setItem(
        KEY,
        JSON.stringify({
          token: saved.token ?? null,
          session: saved.session ?? null,
          deviceId: saved.deviceId,
          /*
           * Everything that exists nowhere else survives. Everything else is a
           * copy of something the server already has and will send again.
           *
           * This kept only `outbox`, and the button says "keeps unsent scores"
           * — but a Nassau struck by handshake on the first tee with no signal
           * lives in `sideOutbox` and exists on no other phone, and a card
           * signed on the 18th green lives in `confirmOutbox`. Both were
           * silently destroyed by the button whose label promises otherwise.
           */
          outbox: saved.outbox ?? [],
          sideOutbox: saved.sideOutbox ?? [],
          confirmOutbox: saved.confirmOutbox ?? [],
          cursor: 0,
          doc: null,
          serverScores: [],
          /*
           * The epoch is CARRIED, not zeroed.
           *
           * Zero is not "unknown" — it is a real epoch, and the live week is
           * past it. So the first poll after the reload saw `epoch !== 0`,
           * took the reset branch, and emptied the outbox this function had
           * just gone to the trouble of preserving. A man whose phone
           * white-screened on the 9th with four holes queued lost all four,
           * with no error and no pending dot.
           */
          scoresEpoch: saved.scoresEpoch ?? 0,
        }),
      )
    } catch {
      // If storage is unreadable there is nothing to preserve anyway.
      try {
        localStorage.removeItem(KEY)
      } catch {
        /* nothing left to try */
      }
    }
    location.reload()
  }

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    const pending = (() => {
      try {
        return (JSON.parse(localStorage.getItem(KEY) ?? '{}').outbox ?? []).length
      } catch {
        return 0
      }
    })()

    return (
      <div className="min-h-dvh bg-paper text-ink p-6 flex flex-col justify-center gap-4">
        <h1 className="bc-display text-2xl">Something broke</h1>
        <p className="text-sm text-mute leading-snug">
          The app hit an error and stopped rather than showing you numbers it could not
          stand behind.
        </p>
        {pending > 0 && (
          <p className="text-sm leading-snug">
            <b className="bc-name">
              {pending} score{pending === 1 ? '' : 's'} on this phone {pending === 1 ? 'has' : 'have'} not
              reached the server yet.
            </b>{' '}
            Reloading keeps them. Start fresh keeps them too.
          </p>
        )}
        <pre className="text-[13px] text-mute whitespace-pre-wrap break-words bg-wash p-3 max-h-40 overflow-auto">
          {error.message}
        </pre>
        <div className="grid gap-2">
          <button
            type="button"
            onClick={() => location.reload()}
            className="bc-tap bg-ink text-paper bc-label py-4"
          >
            Reload
          </button>
          <button
            type="button"
            onClick={this.startFresh}
            className="bc-tap border-2 border-ink bc-label py-3"
          >
            Start fresh — keeps unsent scores
          </button>
        </div>
        <p className="text-[13px] text-mute leading-snug">
          If it keeps happening, someone else&rsquo;s phone almost certainly still works
          — the scores live on the server, not here.
        </p>
      </div>
    )
  }
}
